import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { BookOpenCheck, Info, Loader2, Lock, Quote } from "lucide-react";
import BlurredPreview from "../paywall/BlurredPreview";
import PaywallModal from "../paywall/PaywallModal";

/**
 * ThesisReview — D4 (iter134).
 *
 * Reads `GET /api/coach/thesis/history` and renders reviewed theses
 * (thesis text + deterministic outcome pill/pnl/hold-time).
 * No LLM narration — the numbers are read straight off the closed
 * `coach_normalised_trades` row via `outcome.*`.
 *
 * Tier gating:
 *   * Free  → server returns last 3, and we badge the "Elite unlocks
 *             full history" affordance underneath.
 *   * Elite → server returns last 50; badge hidden.
 *
 * Filter chips (client-side only, deterministic):
 *   Recent (default) · Wins · Losses.
 */
export default function ThesisReview() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [elite, setElite] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [filter, setFilter] = useState("recent");
  const [paywallOpen, setPaywallOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/coach/thesis/history");
        setItems(data?.items || []);
        setElite(!!data?.elite);
        setHiddenCount(data?.hidden_count || 0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "wins")
      return items.filter((i) => i.outcome?.verdict === "won");
    if (filter === "losses")
      return items.filter((i) => i.outcome?.verdict === "lost");
    return items;
  }, [items, filter]);

  return (
    <section
      data-testid="thesis-review-section"
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BookOpenCheck className="w-4 h-4 text-[var(--bt-accent)]" />
          <div>
            <p className="bt-label">Thesis Review</p>
            <h2 className="font-heading text-xl">Your words vs your outcome</h2>
          </div>
        </div>
        <div className="flex items-center gap-1" data-testid="thesis-review-filters">
          {[
            ["recent", "Recent"],
            ["wins", "Wins"],
            ["losses", "Losses"],
          ].map(([k, label]) => (
            <button
              key={k}
              data-testid={`thesis-review-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1 text-xs font-mono uppercase rounded-sm border transition-colors ${
                filter === k
                  ? "border-[var(--bt-accent)] text-[var(--bt-accent)]"
                  : "border-[var(--bt-border)] text-[var(--bt-text-muted)] hover:text-[var(--bt-text)]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--bt-text-muted)] py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="text-sm text-[var(--bt-text-muted)] py-8 text-center border border-dashed border-[var(--bt-border)] rounded-sm"
          data-testid="thesis-review-empty"
        >
          {items.length === 0
            ? "No reviewed theses yet — capture one at your next entry and Coach will pair it with the outcome on close."
            : "No trades match this filter."}
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((t) => (
            <ThesisReviewRow key={t.entry_order_id} thesis={t} />
          ))}
        </ul>
      )}

      {!elite && hiddenCount > 0 && filter === "recent" && (
        <BlurredPreview
          hiddenCount={hiddenCount}
          unit="thesis"
          onUnlock={() => setPaywallOpen(true)}
        >
          <ul className="space-y-3">
            {Array.from({ length: Math.min(hiddenCount, 2) }).map((_, i) => (
              <li
                key={`ph-${i}`}
                data-testid={`thesis-review-placeholder-${i}`}
                className="border border-[var(--bt-border)] rounded-sm p-4 h-24"
              />
            ))}
          </ul>
        </BlurredPreview>
      )}

      {!elite && (
        <div
          data-testid="thesis-review-elite-upsell"
          className="border border-dashed border-[var(--bt-accent)] rounded-sm p-4 flex items-center gap-3 flex-wrap"
        >
          <Lock className="w-4 h-4 text-[var(--bt-accent)]" />
          <p className="text-sm text-[var(--bt-text-muted)] flex-1 min-w-[220px]">
            Free tier shows your last 3 reviewed theses. Elite unlocks the
            full history so patterns emerge over weeks and months.
          </p>
          <Button
            data-testid="thesis-review-upgrade-btn"
            onClick={() => setPaywallOpen(true)}
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-9"
          >
            Upgrade to Elite
          </Button>
        </div>
      )}

      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        trigger="thesis_history"
      />
    </section>
  );
}

function VerdictPill({ verdict }) {
  const map = {
    won: { label: "Won", cls: "text-[var(--bt-accent)] border-[var(--bt-accent)]" },
    lost: { label: "Lost", cls: "text-[var(--bt-loss)] border-[var(--bt-loss)]" },
    breakeven: { label: "Breakeven", cls: "text-[var(--bt-text-muted)] border-[var(--bt-border)]" },
  };
  const v = map[verdict] || map.breakeven;
  return (
    <span
      data-testid={`thesis-verdict-${verdict}`}
      className={`px-2 py-0.5 text-xs font-mono uppercase border rounded-sm ${v.cls}`}
    >
      {v.label}
    </span>
  );
}

function formatHeld(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = seconds / 3600;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function formatPnl(pnl) {
  if (pnl == null) return "—";
  const abs = Math.abs(pnl);
  const rounded = abs >= 1000 ? Math.round(abs).toLocaleString()
                              : abs.toFixed(2);
  return `${pnl < 0 ? "-" : "+"}${rounded}`;
}

function ThesisReviewRow({ thesis }) {
  const o = thesis.outcome || {};
  const pnlColor =
    o.verdict === "won" ? "text-[var(--bt-accent)]"
      : o.verdict === "lost" ? "text-[var(--bt-loss)]"
      : "text-[var(--bt-text-muted)]";
  const isGrossPending = o.pnl_source !== "journal_net";
  // iter134 Fix-11 — close-side orphan detection. When the fresh-entry
  // heuristic fired on an order that FIFO-closed pre-existing lots
  // (Delta's position aggregator briefly reads 0 between reversal
  // cycles), `entry_lot_id` never backfills but `close_lot_count > 0`.
  // Surface the honest framing: this outcome describes the CLOSED
  // lots' P&L, not a fresh entry that "worked".
  const isCloseSideOrphan =
    thesis.entry_lot_id == null && (o.close_lot_count || 0) > 0;
  return (
    <li
      data-testid={`thesis-review-row-${thesis.entry_lot_id || thesis.entry_order_id}`}
      className="border border-[var(--bt-border)] rounded-sm p-4 space-y-2"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <VerdictPill verdict={o.verdict} />
          <p className="font-heading text-lg">
            {thesis.symbol}
            <span className="text-sm text-[var(--bt-text-muted)] font-sans ml-2">
              · {o.direction || "—"}
            </span>
          </p>
        </div>
        <div className="text-right">
          <p className={`font-heading text-lg ${pnlColor}`}>
            {formatPnl(o.realised_pnl)}
          </p>
          <p className="text-xs text-[var(--bt-text-muted)] font-mono flex items-center gap-1 justify-end">
            {isGrossPending ? (
              <span
                data-testid={`thesis-pnl-pending-${thesis.entry_order_id}`}
                className="text-[var(--bt-text-muted)] italic"
                title="Gross P&L shown — fees + funding not yet in the journal. Auto-reconciles on next Delta sync."
              >
                gross (fees pending) ·
              </span>
            ) : (
              <span
                data-testid={`thesis-pnl-net-${thesis.entry_order_id}`}
                className="text-[var(--bt-text-muted)]"
                title="Net P&L — gross minus trading fees plus funding, from the main journal."
              >
                net ·
              </span>
            )}
            {formatHeld(o.held_seconds)} held
          </p>
        </div>
      </div>
      <blockquote
        className="border-l-2 border-[var(--bt-accent)] pl-3 text-sm italic text-[var(--bt-text)]"
        data-testid={`thesis-text-${thesis.entry_order_id}`}
      >
        <Quote className="w-3 h-3 inline mr-1 text-[var(--bt-accent)]" />
        {thesis.thesis_text}
      </blockquote>
      {isCloseSideOrphan && (
        <p
          data-testid={`thesis-close-side-note-${thesis.entry_order_id}`}
          className="text-xs text-[var(--bt-text-muted)] flex items-start gap-2 border-t border-dashed border-[var(--bt-border)] pt-2"
          title="Delta's position aggregator briefly read 0 between reversal cycles, so a fresh-entry prompt fired even though this fill actually closed earlier lots."
        >
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-[var(--bt-text-muted)]" />
          <span>
            This order <b>closed {o.close_lot_count} earlier {o.direction || "lot"}
            {(o.close_lot_count || 0) > 1 ? "s" : ""}</b> —
            the P&amp;L above is those {o.direction || "lot"}s' outcome, not a
            fresh entry that worked.
          </span>
        </p>
      )}
    </li>
  );
}
